import { startOfISOWeek } from 'date-fns';
import { matchPath } from 'react-router';

import {
  ADVISOR_USER_GROUPS, ADVISOR_USERS, AUTHORS, BRANDS, FOLLOW_UP_STATUS, MODE, MODULES,
  NOT_EXIST, SEARCH, SENDERS, SORT, START_DATE_FROM, START_DATE_TO, TAGS
} from 'constants/FilterConstants';
import { TECHNICIAN } from 'constants/ModeConstants';
import { PATH } from 'constants/RoutesConstants';
import * as types from 'modules/followUp/actionTypes';
import { ENABLE_MODES as MODES } from 'modules/followUp/constant';
import { filterSliceAdapter } from 'modules/global/reducers/filterSliceAdapter';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { getPersistedFilters } from 'services/StateLoader';
import { createReducer } from 'utils/ReduxUtils';

/** @typedef {'TECHNICIAN' | 'SALES' | 'PARTS'} FollowUpType */
/**
 * @typedef {
 *  Record<import("modules/followUp/types/action").FilterFollowUpFields, import("modules/followUp/types/action").FilterFollowUpValue>
 * } FilterFollowerState
*/
/**
 * @typedef {{
 *   mode: import("modules/global/typing/enums/modes.enum").ModeEnum,
 * } & Record<FollowUpType,FilterFollowerState>} FollowUpFiltersReducer
 */

const emptyState = {
  [SEARCH]: null,
  [SORT]: 'createdOn,DESC',
  [START_DATE_FROM]: startOfISOWeek(new Date()).toISOString(),
  [START_DATE_TO]: new Date().toISOString(),
  [MODULES]: [],
  [BRANDS]: [],
  [TAGS]: [],
  [AUTHORS]: [],
  [SENDERS]: [],
  [NOT_EXIST]: [],
  [ADVISOR_USERS]: [],
  [ADVISOR_USER_GROUPS]: [],
  [FOLLOW_UP_STATUS]: []
};

const persistedFilters = getPersistedFilters()?.filters?.followUpDashboard ?? {};

// Migration: if startDateFrom was null (old "current file" option),
const migratedFilters = MODES.reduce((acc, mode) => {
  if (persistedFilters[mode]?.[START_DATE_FROM] === null) {
    const { [START_DATE_FROM]: _, ...rest } = persistedFilters[mode];
    return { ...acc, [mode]: rest };
  }
  return { ...acc, [mode]: persistedFilters[mode] ?? {} };
}, {});

const initialState = MODES.reduce((acc, mode) => ({ ...acc, [mode]: emptyState }), { [MODE]: TECHNICIAN });

/** @type {import("modules/global/reducers/filterSliceAdapter").FilterAdapter<FollowUpFiltersReducer>} */
const filterAdapter = filterSliceAdapter(initialState, migratedFilters);

const bootloader = (state, { payload }) => {
  const match = matchPath(PATH.FOLLOW_UP.path, payload.location.pathname);
  const paramsMode = match.params.mode.toUpperCase();
  const mode = MODES.includes(paramsMode) ? paramsMode : TECHNICIAN;

  return {
    ...state,
    [MODE]: mode,
  };
};

const applyReducerToMode = fn => (state, action) => {
  const mode = state[MODE];
  const slice = state[mode];

  return {
    ...state,
    [mode]: fn(slice, action),
  };
};

const resetFilters = (state, action) => {
  const mode = state[MODE];
  const { [mode]: clearState, } = filterAdapter.resetFilters(state, action);

  return {
    ...state,
    [mode]: { ...clearState, [SEARCH]: state[mode][SEARCH] },
  };
};

export default createReducer(
  filterAdapter.getInitialState(),
  {
    [types.CLEAR_FOLLOW_UP_DASHBOARD_FILTER]: resetFilters,
    [types.SET_FOLLOW_UP_DASHBOARD_FILTER]: applyReducerToMode(filterAdapter.setFilter),
    [types.SET_FOLLOW_UP_DASHBOARD_FILTERS]: applyReducerToMode(filterAdapter.setFilters),
    [types.REMOVE_FOLLOW_UP_DASHBOARD_FILTER]: applyReducerToMode(filterAdapter.removeFilter),
  },
  [
    {
      matcher: ({ type, payload }) => type === LOCATION_CHANGE && matchPath(PATH.FOLLOW_UP.path, payload.location.pathname),
      reducer: bootloader,
    },
  ]
);
